import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatchApp } from "../../../lib/redux";
import { startSendGlobalAgentMessage } from "../../../actions/chat";
import { getRoleCapability } from "../../../actions/getters";
import { useReportSnapshot } from "../../../features/reportPdf/ReportSnapshotContext";
import { useTranslationApp } from "../../../lib/i18next";
import {
  BRAND_INSIGHTS_APPLY_CONFIRM,
  GLOBAL_REPORT_AGENT_INSIGHTS,
  GLOBAL_REPORT_INSIGHTS_ERROR,
  GLOBAL_REPORT_NEW_INSIGHTS,
  GLOBAL_REPORT_NO_INSIGHTS,
} from "../../../i18n/keysTranslations";
import {
  AUTOMATIC_AGENT_REQUEST_KINDS,
  BORDER_RADIUS,
  BRAND_INSIGHT_APPLY_SOURCE_GLOBAL_REPORT,
} from "../../../utils/constants";
import Button from "../../../components/Buttons/Button";
import ApplyInsightModal from "../../Settings/BrandInsights/components/ApplyInsightModal";
import { useApplyBrandInsight } from "../../Settings/BrandInsights/hooks/useApplyBrandInsight";
import { getLanguageFormatted } from "../../../utils/string";
import InsightCard from "./InsightCard";

import { LaunchError } from "../../../utils/errors";

const APPLY_BUTTON_SX = {
  minWidth: 0,
  width: "auto",
  height: 36,
  fontSize: "13px !important",
  px: 2,
};

const SEVERITY_ORDER = ["positive", "warning", "critical"];

const groupInsightsBySeverity = (insights) => {
  const grouped = {
    positive: [],
    warning: [],
    critical: [],
  };

  insights.forEach((insight) => {
    const severity = grouped[insight.severity] ? insight.severity : "warning";
    grouped[severity].push(insight);
  });

  return grouped;
};

const GlobalReportInsightsPanel = ({
  globalReportContext,
  agentRequestKey,
  isReady = false,
  onInsightsGenerated = () => {},
  onAiSettledChange = () => {},
}) => {
  const { t, i18n } = useTranslationApp();
  const dispatch = useDispatchApp();
  const canEditBrandSetup = dispatch(getRoleCapability("canEditBrandSetup"));
  const {
    applyInsight,
    proposal,
    isProposing,
    isConfirming,
    applyError,
    openApply,
    closeApply,
    confirmApply,
    retryApply,
  } = useApplyBrandInsight();
  const snapshot = useReportSnapshot();
  const [insights, setInsights] = useState(snapshot?.insights ?? []);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [insightsError, setInsightsError] = useState(false);
  const fetchIdRef = useRef(0);
  const lastFetchedKeyRef = useRef(null);

  useEffect(() => {
    if (snapshot) {
      onInsightsGenerated(snapshot.insights ?? []);
      onAiSettledChange(true);
      return;
    }

    if (!isReady || !globalReportContext || !agentRequestKey) {
      onAiSettledChange(false);
      return;
    }

    const agentFetchKey = `${agentRequestKey}|${i18n.language}`;
    if (lastFetchedKeyRef.current === agentFetchKey) {
      onAiSettledChange(true);
      return;
    }
    lastFetchedKeyRef.current = agentFetchKey;

    const fetchId = ++fetchIdRef.current;
    setIsLoadingInsights(true);
    setInsightsError(false);
    setInsights([]);

    dispatch(
      startSendGlobalAgentMessage({
        globalReportContext,
        // Passive panel: server owns the prompt for this kind and does not bill it.
        automaticRequest: {
          kind: AUTOMATIC_AGENT_REQUEST_KINDS.GLOBAL_REPORT_INSIGHTS,
          language: getLanguageFormatted(i18n.language),
        },
      })
    )
      .then((response) => {
        if (fetchId !== fetchIdRef.current) {
          return;
        }

        if (response?.ok) {
          const nextInsights = response.data?.[0]?.insights || [];
          setInsights(nextInsights);
          onInsightsGenerated(nextInsights);
        } else {
          setInsightsError(true);
          onInsightsGenerated([]);
        }
      })
      .catch((error) => {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "GlobalReportInsightsPanel",
            capture: "exception",
          })
        );
        console.error("Error generating global report insights:", error);
        if (fetchId === fetchIdRef.current) {
          setInsightsError(true);
          onInsightsGenerated([]);
        }
      })
      .finally(() => {
        if (fetchId === fetchIdRef.current) {
          setIsLoadingInsights(false);
          onAiSettledChange(true);
        }
      });
  }, [
    agentRequestKey,
    dispatch,
    globalReportContext,
    i18n.language,
    isReady,
    onAiSettledChange,
    onInsightsGenerated,
    snapshot,
    t,
  ]);

  useEffect(() => {
    return () => onAiSettledChange(false);
  }, [onAiSettledChange]);

  const hasInsights = insights.length > 0;

  const insightsBySeverity = useMemo(
    () => groupInsightsBySeverity(insights),
    [insights]
  );

  const severityColumns = useMemo(
    () =>
      SEVERITY_ORDER.map((severity) => ({
        severity,
        items: insightsBySeverity[severity],
      })).filter((column) => column.items.length > 0),
    [insightsBySeverity]
  );

  if (!globalReportContext) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t(GLOBAL_REPORT_AGENT_INSIGHTS)}
        </Typography>
        {!isLoadingInsights && isReady && hasInsights && (
          <Chip
            label={t(GLOBAL_REPORT_NEW_INSIGHTS, {
              count: insights.length,
            })}
            size="small"
            color="primary"
            sx={{ fontWeight: 600 }}
          />
        )}
      </Box>

      <Box
        data-pdf-insights-grid
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          },
          gap: 1.5,
          width: "100%",
        }}
      >
        {isLoadingInsights || !isReady ? (
          Array.from({ length: 3 }).map((_, index) => (
            <Box
              key={`insight-skeleton-column-${index}`}
              sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
            >
              <Skeleton
                variant="rounded"
                height={72}
                sx={{ borderRadius: `${BORDER_RADIUS * 2}px` }}
              />
              <Skeleton
                variant="rounded"
                height={72}
                sx={{ borderRadius: `${BORDER_RADIUS * 2}px` }}
              />
            </Box>
          ))
        ) : insightsError ? (
          <Typography
            variant="body2"
            color="error"
            sx={{ gridColumn: "1 / -1" }}
          >
            {t(GLOBAL_REPORT_INSIGHTS_ERROR)}
          </Typography>
        ) : hasInsights ? (
          severityColumns.map(({ severity, items }) => (
            <Box
              key={severity}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                minWidth: 0,
              }}
            >
              {items.map((insight) => (
                <InsightCard
                  key={insight.id}
                  insight={insight}
                  action={
                    canEditBrandSetup ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          mt: "auto",
                        }}
                      >
                        <Button
                          color="secondary"
                          onClick={() =>
                            openApply(
                              {
                                title: insight.title,
                                description: insight.description,
                                severity: insight.severity,
                              },
                              {
                                source:
                                  BRAND_INSIGHT_APPLY_SOURCE_GLOBAL_REPORT,
                              }
                            )
                          }
                          sx={APPLY_BUTTON_SX}
                        >
                          {t(BRAND_INSIGHTS_APPLY_CONFIRM)}
                        </Button>
                      </Box>
                    ) : null
                  }
                />
              ))}
            </Box>
          ))
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ gridColumn: "1 / -1" }}
          >
            {t(GLOBAL_REPORT_NO_INSIGHTS)}
          </Typography>
        )}
      </Box>
      <ApplyInsightModal
        modalOpen={Boolean(applyInsight)}
        isProposing={isProposing}
        isConfirming={isConfirming}
        proposal={proposal}
        applyError={applyError}
        onCloseModal={closeApply}
        onConfirm={confirmApply}
        onRetry={retryApply}
      />
    </Box>
  );
};

export default GlobalReportInsightsPanel;
